import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy.js';
import { FormattedMessage } from 'dibs-react-intl';
import classNames from 'classnames';

import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea.tsx';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton.tsx';

import styles from './PdpSharedDescription.scss';

export const Title = ({ isDyoCustomizable, isJewelry }) => {
    const defaultTitle = (
        <FormattedMessage
            id="PdpSharedDescriptionAdditionalOptions.customizationHeader"
            defaultMessage="Customization"
        />
    );

    const jewelryTitle = (
        <FormattedMessage
            id="PdpSharedDescriptionAdditionalOptions.customizationHeaderJewelry"
            defaultMessage="Custom Services"
        />
    );

    const dyoTitle = (
        <FormattedMessage
            id="PdpSharedDescriptionAdditionalOptions.designYourOwnHeader"
            defaultMessage="Request Customization"
        />
    );

    if (isDyoCustomizable) {
        return dyoTitle;
    }

    return isJewelry ? jewelryTitle : defaultTitle;
};

Title.propTypes = {
    isDyoCustomizable: PropTypes.bool,
    isJewelry: PropTypes.bool,
};

export const designYourOwnMessage = (
    <FormattedMessage
        id="PdpSharedDescriptionAdditionalOptions.designYouOwn"
        defaultMessage="This gemstone can be customized into an extraordinary work of art under your direction. Collaborate with the jeweler to create a one-of-a-kind piece. Simply complete the form provided, and the seller will contact you."
    />
);

const PdpSharedDescriptionAdditionalOptionsContentComponent = ({
    customizationOptions,
    contentClass,
    contentSize,
    isResizable,
    item,
    user,
    isDyoCustomizable,
    isFullWidthCta = false,
    buttonType,
    buttonWrapperClassName,
    collapsedHeight,
    disableExpansion,
    buttonSize = 'small',
}) => {
    const [contentHeight, setContentHeight] = useState(null);
    const setContentRef = useCallback(node => {
        if (node) {
            setContentHeight(node.getBoundingClientRect().height);
        }
    }, []);

    const COLLAPSED_CONTENT_HEIGHT = collapsedHeight || 120;

    const shouldBeExpandable =
        !disableExpansion && !isDyoCustomizable && contentHeight >= COLLAPSED_CONTENT_HEIGHT;
    const showProductionTime =
        item?.seller?.isJewelrySeller && item?.isCustomizable && item?.leadTimeDisplay;

    const resizableContent = isResizable ? (
        <div>
            <FormattedMessage
                id="PdpSharedDescriptionAdditionalOptions.resizingAvailable"
                defaultMessage="Resizing is available."
            />
        </div>
    ) : null;

    const dyoCopy = isDyoCustomizable ? <>{designYourOwnMessage}</> : null;

    const content = (
        <span
            className={classNames(styles.content, contentClass, {
                [styles.small]: contentSize === 'small',
            })}
            ref={setContentRef}
        >
            {resizableContent}
            {customizationOptions}
            {dyoCopy}
            {showProductionTime && (
                <div>
                    <span className={styles.productionTime}>
                        <FormattedMessage
                            id="PdpSharedDescriptionAdditionalOptions.productionTime"
                            defaultMessage="Estimated Production Time"
                        />
                    </span>
                    {': '}
                    {item.leadTimeDisplay}
                </div>
            )}
        </span>
    );

    const launchOrigin = isDyoCustomizable
        ? 'dyo secondary cta'
        : 'contact link in pdp - description';

    const contactButton = (
        <div
            className={classNames({
                // If contact button just being displayed as link, don't include padding added to button ctas
                [styles.ctaWrapper]: buttonType !== 'link',
            })}
        >
            <PdpSharedContactDealerButton
                size={buttonSize}
                fullWidth={isFullWidthCta}
                buttonType={buttonType || 'secondary'}
                wrapperClassName={buttonWrapperClassName}
                launchOrigin={launchOrigin}
                type={'REQUEST_CUSTOMIZATION'}
                item={item}
                user={user}
            />
        </div>
    );
    return shouldBeExpandable ? (
        <>
            <ExpandingAreaWithReadMore
                collapsedHeight={COLLAPSED_CONTENT_HEIGHT}
                showGradient={false}
                wrapperClass={styles.expandingAreaWrapper}
            >
                {content}
            </ExpandingAreaWithReadMore>
            {contactButton}
        </>
    ) : (
        <>
            {content}
            {contactButton}
        </>
    );
};

PdpSharedDescriptionAdditionalOptionsContentComponent.propTypes = {
    customizationOptions: PropTypes.string,
    contentClass: PropTypes.string,
    contentSize: PropTypes.string,
    isDyoCustomizable: PropTypes.bool,
    isResizable: PropTypes.bool,
    item: PropTypes.object,
    user: PropTypes.object,
    isFullWidthCta: PropTypes.bool,
    buttonType: PropTypes.string,
    collapsedHeight: PropTypes.number,
    disableExpansion: PropTypes.bool,
    buttonSize: PropTypes.string,
    buttonWrapperClassName: PropTypes.string,
};

export const PdpSharedDescriptionAdditionalOptionsContent = createFragmentContainer(
    PdpSharedDescriptionAdditionalOptionsContentComponent,
    {
        user: graphql`
            fragment PdpSharedDescriptionAdditionalOptionsContent_user on User {
                ...PdpSharedContactDealerButton_user
            }
        `,
        item: graphql`
            fragment PdpSharedDescriptionAdditionalOptionsContent_item on Item {
                ...PdpSharedContactDealerButton_item
                leadTimeDisplay
                isCustomizable
                seller {
                    isJewelrySeller
                }
            }
        `,
    }
);
